import React, { useEffect, useState } from 'react';
import { Button } from '@/ui/components/button';
import { ApiError, hasSignedInHint, v2 } from '../../screens/account/api';
import { SignedOutError, v2Session } from '../lists/lists';
import { countLabel } from '../../screens/profile/follows';

/**
 * Follow a restaurant (prototype RestaurantDetail Follow): the follower count for everyone,
 * and for signed-in diners a Follow / Following button, optimistic and put back if the call
 * fails. Asked after the page loads, so the restaurant page's server render stays as it is.
 * Only with ENABLE_FOLLOWS and ENABLE_ACCOUNTS (api-next flag restaurant_follows).
 */

export const RESTAURANT_FOLLOW = `query RestaurantFollow($id: ID!, $signedIn: Boolean!) {
    restaurantFollowerCount(legacyRestaurantId: $id)
    isFollowingRestaurant(legacyRestaurantId: $id) @include(if: $signedIn)
}`;
const FOLLOW = `mutation FollowRestaurant($id: ID!) { followRestaurant(legacyRestaurantId: $id) }`;
const UNFOLLOW = `mutation UnfollowRestaurant($id: ID!) { unfollowRestaurant(legacyRestaurantId: $id) }`;

const PlusIcon = () => (
    <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        className="size-4"
    >
        <path d="M12 5v14M5 12h14" />
    </svg>
);

export const RestaurantFollow = ({
    legacyRestaurantId,
}: {
    legacyRestaurantId: string;
}) => {
    const [count, setCount] = useState<number | null>(null);
    const [signedIn, setSignedIn] = useState(false);
    const [following, setFollowingState] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const signed = hasSignedInHint();
        setSignedIn(signed);
        let cancelled = false;
        v2<{
            restaurantFollowerCount: number;
            isFollowingRestaurant?: boolean;
        }>(RESTAURANT_FOLLOW, { id: legacyRestaurantId, signedIn: signed })
            .then(d => {
                if (cancelled) return;
                setCount(d.restaurantFollowerCount);
                setFollowingState(Boolean(d.isFollowingRestaurant));
            })
            // The count is extra: a failure just leaves it out.
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [legacyRestaurantId]);

    const toggle = async () => {
        const next = !following;
        setBusy(true);
        setError('');
        setFollowingState(next);
        setCount(c => (c === null ? c : Math.max(0, c + (next ? 1 : -1))));
        try {
            await v2Session(next ? FOLLOW : UNFOLLOW, {
                id: legacyRestaurantId,
            });
        } catch (err) {
            setFollowingState(!next);
            setCount(c => (c === null ? c : Math.max(0, c + (next ? -1 : 1))));
            setError(
                err instanceof SignedOutError
                    ? 'Your session has ended. Log in again to follow restaurants.'
                    : err instanceof ApiError && err.code === 'BUSINESS_RULE'
                      ? err.message
                      : next
                        ? "Couldn't follow. Please try again."
                        : "Couldn't unfollow. Please try again.",
            );
        } finally {
            setBusy(false);
        }
    };

    if (!signedIn && !count) return null;
    return (
        <div className="flex flex-wrap items-center gap-3">
            {signedIn && (
                <Button
                    variant={following ? 'outline' : 'primary'}
                    size="md"
                    aria-pressed={following}
                    disabled={busy}
                    onClick={toggle}
                >
                    {!following && <PlusIcon />}
                    {following ? 'Following' : 'Follow'}
                </Button>
            )}
            {!!count && (
                <span className="text-body-2 text-ink-muted">
                    <strong className="font-semibold text-ink">
                        {count.toLocaleString('en-AU')}
                    </strong>{' '}
                    {countLabel(count, 'follower', 'followers').replace(
                        /^[\d,]+ /,
                        '',
                    )}
                </span>
            )}
            {error && (
                <p
                    role="alert"
                    className="w-full text-caption text-destructive"
                >
                    {error}
                </p>
            )}
        </div>
    );
};
